/* =========================================================================
   AYNA · Nails — Editorial motion module.
   GSAP-driven reveals + page indicator sync + gallery interactions.
   No Three.js.
   ========================================================================= */
import { gsap } from "gsap";

const prefersReducedMotion = window.matchMedia(
	"(prefers-reduced-motion: reduce)",
).matches;

/* Page indicator: sync active spread with scroll position */
export function initPageIndicator(): void {
	const indicator = document.querySelector<HTMLElement>(".page-indicator");
	const magazine = document.getElementById("magazine");
	if (!indicator || !magazine) return;

	const items = Array.from(
		indicator.querySelectorAll<HTMLLIElement>("ol > li"),
	);
	const currEl = indicator.querySelector<HTMLElement>(".indicator-meta .curr");
	const spreads = Array.from(document.querySelectorAll<HTMLElement>(".spread"));

	const setActive = (idx: number): void => {
		items.forEach((li, i) => li.classList.toggle("is-active", i === idx));
		if (currEl) {
			currEl.textContent = String(idx + 1).padStart(2, "0");
		}
		const spread = spreads[idx];
		if (spread) {
			triggerSpreadReveal(spread);
		}
	};

	const observer = new IntersectionObserver(
		(entries) => {
			let topMost: { idx: number; ratio: number } | null = null;
			for (const e of entries) {
				if (!e.isIntersecting) continue;
				const target = e.target as HTMLElement;
				const idx = spreads.indexOf(target);
				if (idx < 0) continue;
				if (!topMost || e.intersectionRatio > topMost.ratio) {
					topMost = { idx, ratio: e.intersectionRatio };
				}
			}
			if (topMost) setActive(topMost.idx);
		},
		{
			root: magazine,
			threshold: [0.35, 0.5, 0.75],
		},
	);
	spreads.forEach((s) => observer.observe(s));

	items.forEach((li, idx) => {
		const a = li.querySelector<HTMLAnchorElement>("a");
		if (!a) return;
		a.addEventListener("click", (ev) => {
			ev.preventDefault();
			const target = spreads[idx];
			if (target) {
				magazine.scrollTo({
					top: target.offsetTop,
					behavior: prefersReducedMotion ? "auto" : "smooth",
				});
			}
		});
	});

	setActive(0);
}

const revealedSpreads = new WeakSet<HTMLElement>();

function triggerSpreadReveal(spread: HTMLElement): void {
	if (revealedSpreads.has(spread)) return;
	revealedSpreads.add(spread);

	const paragraphs = spread.querySelectorAll<HTMLElement>("[data-reveal]");
	paragraphs.forEach((p, i) => {
		gsap.delayedCall(prefersReducedMotion ? 0 : 0.15 * i, () => {
			p.classList.add("is-revealed");
		});
	});

	const quotes = spread.querySelectorAll<HTMLElement>("[data-quote]");
	quotes.forEach((q, i) => {
		gsap.delayedCall(prefersReducedMotion ? 0 : 0.25 * i, () => {
			q.classList.add("is-revealed");
		});
	});

	const sig = spread.querySelector<HTMLElement>(".signature");
	if (sig) {
		gsap.delayedCall(prefersReducedMotion ? 0 : 1.4, () => {
			sig.classList.add("is-revealed");
		});
	}
}

/* Cover parallax on cursor */
export function initCoverParallax(): void {
	if (prefersReducedMotion) return;
	const cover = document.querySelector<HTMLElement>(
		".spread--cover .cover-photo[data-parallax]",
	);
	const coverSpread = document.querySelector<HTMLElement>(".spread--cover");
	if (!cover || !coverSpread) return;

	let raf = 0;
	let tx = 0;
	let ty = 0;
	let cx = 0;
	let cy = 0;

	const update = (): void => {
		cx += (tx - cx) * 0.08;
		cy += (ty - cy) * 0.08;
		cover.style.transform = `translate3d(${cx}px, ${cy}px, 0) scale(1.05)`;
		raf = requestAnimationFrame(update);
	};

	coverSpread.addEventListener("pointermove", (ev) => {
		const rect = coverSpread.getBoundingClientRect();
		const px = (ev.clientX - rect.left) / rect.width - 0.5;
		const py = (ev.clientY - rect.top) / rect.height - 0.5;
		tx = px * -16;
		ty = py * -16;
		if (!raf) raf = requestAnimationFrame(update);
	});

	coverSpread.addEventListener("pointerleave", () => {
		tx = 0;
		ty = 0;
	});
}

/* Services list: hover swaps right-side photo */
export function initServicesInteraction(): void {
	const items = document.querySelectorAll<HTMLLIElement>(
		"[data-services] > li",
	);
	const frames = document.querySelectorAll<HTMLElement>("[data-img-frame]");
	if (!items.length || !frames.length) return;

	const setActive = (idx: number): void => {
		frames.forEach((f) => {
			const fi = parseInt(f.dataset.imgFrame ?? "-1", 10);
			f.classList.toggle("is-active", fi === idx);
		});
	};

	items.forEach((li) => {
		const idx = parseInt(li.dataset.img ?? "0", 10);
		li.addEventListener("pointerenter", () => setActive(idx));
		li.addEventListener("focus", () => setActive(idx));
	});
}

/* Gallery: drag-to-scroll + wheel-to-horizontal */
export function initGalleryInteraction(): void {
	const track = document.querySelector<HTMLElement>("[data-gallery]");
	if (!track) return;

	track.addEventListener(
		"wheel",
		(ev) => {
			if (Math.abs(ev.deltaY) < Math.abs(ev.deltaX)) return;
			const maxScroll = track.scrollWidth - track.clientWidth;
			if (maxScroll <= 0) return;
			const atStart = track.scrollLeft <= 0 && ev.deltaY < 0;
			const atEnd = track.scrollLeft >= maxScroll - 1 && ev.deltaY > 0;
			if (atStart || atEnd) return;
			ev.preventDefault();
			track.scrollLeft += ev.deltaY;
		},
		{ passive: false },
	);

	let isDown = false;
	let startX = 0;
	let scrollStart = 0;
	track.addEventListener("pointerdown", (ev) => {
		isDown = true;
		startX = ev.clientX;
		scrollStart = track.scrollLeft;
		track.setPointerCapture(ev.pointerId);
	});
	track.addEventListener("pointermove", (ev) => {
		if (!isDown) return;
		const dx = ev.clientX - startX;
		track.scrollLeft = scrollStart - dx;
	});
	const endDrag = (ev: PointerEvent): void => {
		isDown = false;
		try {
			track.releasePointerCapture(ev.pointerId);
		} catch {
			/* noop */
		}
	};
	track.addEventListener("pointerup", endDrag);
	track.addEventListener("pointercancel", endDrag);
}
